import React, { Fragment, useMemo, useEffect } from "react";
import { get, noop } from "lodash";

import Icon from "../../_static/Icons";
import {
  contests,
  refresh,
  arrowDownward,
  arrowForward
} from "../../_static/Icons/icons";
import buildColor from "../../_static/ColorPalette";
import Button from "../../_atom/Buttons/button";
import {
  PendingLimitsSeparator,
  Container,
  LimitDescription,
  IconWrapper,
  IconWrapperSlim,
  LimitsInfo,
  LimitValue,
  LimitStatus,
  LimitSeparator,
  LimitSeparatorLine,
  PendingLimitDateDescription,
  CurrentPendingContainer,
  PendingContainer
} from "./styled-components";

const renderLimitBaseInfo = (limitValue, limitType, limitLabel) => {
  const isCurrentType = limitType === "current";
  return (
    <LimitDescription>
      <IconWrapper
        isCurrentType={isCurrentType}
        data-qa-label={`icon-wrapper-${limitType}`}
      >
        <Icon
          qaLabel={`${limitType}LimitIcon`}
          icon={isCurrentType ? contests : refresh}
          color={
            isCurrentType
              ? buildColor("grey", "600")
              : buildColor("orange", "500")
          }
          size={18}
        />
      </IconWrapper>
      <LimitsInfo>
        <LimitValue data-qa-label={`${limitType}Limit`}>
          {limitValue}
        </LimitValue>
        <LimitStatus data-qa-label={`${limitType}LimitStatus`}>
          {limitLabel}
        </LimitStatus>
      </LimitsInfo>
    </LimitDescription>
  );
};

const renderPendingLimitDescription = (
  pendingLimitDate,
  appliedLabel,
  rightAlignment = true
) => (
  <PendingLimitDateDescription>
    <LimitValue
      data-qa-label="pendingLimitDate"
      rightAlignment={rightAlignment}
    >
      {pendingLimitDate}
    </LimitValue>
    <LimitStatus
      data-qa-label="pendingLimitStatus"
      rightAlignment={rightAlignment}
    >
      {appliedLabel}
    </LimitStatus>
  </PendingLimitDateDescription>
);

const renderEditCancelBtn = (
  editButtonText = "",
  cancelButtonText = "",
  isInputActive = false,
  toggleInputCallback = noop
) => (
  <Button
    qaLabel="editLimitButton"
    type="secondary"
    size="big"
    onClick={() => toggleInputCallback(!isInputActive)}
    className="editLimitBtn"
  >
    {isInputActive ? cancelButtonText : editButtonText}
  </Button>
);

const PendingLimits = (props) => {
  useEffect(() => {
    if (get(props, "currentLimit.value", false))
      props.toggleInputCallback(false);
    else props.toggleInputCallback(true);
  }, [JSON.stringify(props.currentLimit)]);

  const currentLimitValue = get(props, "currentLimit.value");
  const { value: pendingLimitValue, pendingLimitDate } = get(
    props,
    "pendingLimit"
  );

  return useMemo(
    () =>
      currentLimitValue &&
      (props.device === "mobile" ? (
        <Fragment>
          <PendingLimitsSeparator />
          <Container>
            {renderLimitBaseInfo(
              currentLimitValue,
              "current",
              props.currentLimitLabel
            )}
            {renderEditCancelBtn(
              props.editButtonText,
              props.cancelButtonText,
              props.isInputActive,
              props.toggleInputCallback
            )}
          </Container>
          {pendingLimitValue && (
            <Fragment>
              <LimitSeparator data-qa-label="lineSeparator">
                <Icon
                  qaLabel="arrowDownIcon"
                  icon={arrowDownward}
                  color={buildColor("blue", "200")}
                  size={16}
                />
                <LimitSeparatorLine />
              </LimitSeparator>
              <Container>
                {renderLimitBaseInfo(
                  pendingLimitValue,
                  "pending",
                  props.pendingLimitLabel
                )}
                {renderPendingLimitDescription(
                  pendingLimitDate,
                  props.appliedLabel
                )}
              </Container>
            </Fragment>
          )}
        </Fragment>
      ) : (
        <Fragment>
          <PendingLimitsSeparator />
          <Container>
            <CurrentPendingContainer>
              {renderLimitBaseInfo(
                currentLimitValue,
                "current",
                props.currentLimitLabel
              )}
              {get(props, "pendingLimit.value", false) && (
                <Fragment>
                  <IconWrapperSlim>
                    <Icon
                      qaLabel="arrowForwardIcon"
                      icon={arrowForward}
                      color={buildColor("blue", "200")}
                      size={16}
                    />
                  </IconWrapperSlim>
                  <PendingContainer>
                    {renderLimitBaseInfo(
                      pendingLimitValue,
                      "pending",
                      props.pendingLimitLabel
                    )}
                    {renderPendingLimitDescription(
                      pendingLimitDate,
                      props.appliedLabel,
                      false
                    )}
                  </PendingContainer>
                </Fragment>
              )}
            </CurrentPendingContainer>
            {renderEditCancelBtn(
              props.editButtonText,
              props.cancelButtonText,
              props.isInputActive,
              props.toggleInputCallback
            )}
          </Container>
        </Fragment>
      )),
    [
      props.device,
      JSON.stringify(props.currentLimit),
      JSON.stringify(props.pendingLimit),
      props.isInputActive
    ]
  );
};

PendingLimits.defaultProps = {
  device: "mobile",
  isInputActive: false,
  toggleInputCallback: noop,
  currentLimit: {
    value: ""
  },
  pendingLimit: {
    value: "",
    pendingLimitDate: ""
  },
  currentLimitLabel: "",
  pendingLimitLabel: "",
  appliedLabel: "",
  editButtonText: "",
  cancelButtonText: ""
};

export default PendingLimits;
